import { RadioGroup, Tab } from "@headlessui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import Button from "~/components/common/button";
import Dialog from "~/components/common/dialog";
import Icon from "~/components/common/icon";
import { useWishlist } from "~/contexts/wishlist";
import { wishlistIdType } from "~/shared/wishlist/wishlist.model";
import { createNewWishlist } from "~/shared/wishlist/zod-schemas.class";

import CartCreateWishlistForm from "./cart-create-wishlist-form";
import styles from "./cart-wishlist-dialog.module.scss";

type Props = {
  productId: number;
  isOpen: boolean;
  onClose: (destinationListId?: number | string) => void;
  savedForLater?: boolean;
};

export default function CartWishlistDialog({ productId, isOpen, onClose, savedForLater }: Props) {
  const t = useTranslations();
  const [tabIsClicked, setTabIsClicked] = useState(false);

  const { list, removeProdFromWishlist, addProdToWishlist, savedForLaterList } = useWishlist();

  const [moveToWishlistError, setMoveToWishlistError] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectWishlistValidationSchema = createNewWishlist(list);
  type ValidationSchema = z.infer<typeof selectWishlistValidationSchema>;

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting, errors },
  } = useForm<ValidationSchema>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldFocusError: false,
    resolver: zodResolver(selectWishlistValidationSchema),
  });

  useEffect(() => {
    if (moveToWishlistError) {
      setTimeout(() => {
        setMoveToWishlistError("");
      }, 5000);
    }
  }, [moveToWishlistError]);

  const submit = async (data: ValidationSchema) => {
    const response = await addProdToWishlist(productId, data.wishlistId as wishlistIdType);
    if (response.errorMessage) {
      setMoveToWishlistError(response.errorMessage);
    } else {
      if (savedForLater && savedForLaterList) {
        await removeProdFromWishlist(productId, savedForLaterList.ID);
      }
      onClose(data.wishlistId as wishlistIdType);
    }
  };

  const handleCreateSubmit = (id: number) => {
    onClose(id);
  };

  return (
    <Dialog
      isOpen={isOpen}
      title={
        <>
          <Icon name="heart" />
          <span>{t("generic.myaccount.wishlist.select_wishlist")}</span>
        </>
      }
      onClose={onClose}
      customStyles={styles}
    >
      <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
        <Tab.List className={styles.tabList}>
          <Tab
            className={classNames(styles.tab, tabIsClicked ? styles.clickedTab : undefined)}
            onClick={() => setTabIsClicked(true)}
            onKeyUp={(e) => (e.key === "Tab" ? setTabIsClicked(false) : undefined)}
          >
            {({ selected }) => (
              <>
                <p>{t("generic.chooseWishlist")}</p>
                {selected && (
                  <motion.div
                    className={styles.selectedBg}
                    exit={{ x: 100 }}
                    animate={{ x: 0 }}
                    initial={{ x: 100 }}
                    transition={{
                      bounce: 0,
                    }}
                  />
                )}
              </>
            )}
          </Tab>
          <Tab
            className={classNames(styles.tab, tabIsClicked ? styles.clickedTab : undefined)}
            onClick={() => setTabIsClicked(true)}
            onKeyUp={(e) => (e.key === "Tab" ? setTabIsClicked(false) : undefined)}
          >
            {({ selected }) => (
              <>
                <p>{t("generic.wishlistAddNew")}</p>
                {selected && (
                  <motion.div
                    className={styles.selectedBg}
                    exit={{ x: -100 }}
                    animate={{ x: 0 }}
                    initial={{ x: -100 }}
                    transition={{
                      bounce: 0,
                    }}
                  />
                )}
              </>
            )}
          </Tab>
        </Tab.List>
        <Tab.Panels className={styles.panels}>
          <Tab.Panel>
            <form onSubmit={handleSubmit(submit)} className={styles.form}>
              <Controller
                control={control}
                name="wishlistId"
                render={({ field }) => (
                  <RadioGroup {...field} className={styles.radioList}>
                    {list?.map((el) => (
                      <RadioGroup.Option key={el.ID} value={el.ID} className={styles.option}>
                        {({ checked }) => (
                          <>
                            <Icon name={checked ? "radio-selected" : "radio-unselected"} width={20} height={20} />
                            <RadioGroup.Label as="p" className={styles.optionLabel}>
                              {el.Name} ({el?.count})
                            </RadioGroup.Label>
                          </>
                        )}
                      </RadioGroup.Option>
                    ))}
                  </RadioGroup>
                )}
              />
              {/* handle errors */}
              {errors.wishlistId && <p className={styles.error}>{t("generic.needselect")}</p>}
              {moveToWishlistError && <p className={styles.error}>{moveToWishlistError}</p>}
              <Button
                disabled={!isValid}
                aria-disabled={!isValid}
                loading={isSubmitting}
                className={styles.confirmButton}
                variant="primary"
                type="submit"
              >
                {t("generic.myaccount.wishlist.save_into")}
              </Button>
            </form>
          </Tab.Panel>

          <Tab.Panel>
            <CartCreateWishlistForm onSubmit={handleCreateSubmit} productId={productId} />
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </Dialog>
  );
}
